import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchUserCompetenciesCacheKey,
  FetchUserCompetenciesFilters,
  FetchUserCompetenciesGqlQuery,
  FetchUserCompetenciesLimit,
  FetchUserCompetenciesPage,
  FetchUserCompetenciesSort
} from '../../userCompetenciesTypes';
import { FetchCacheUserCompetenciesQueryResponse } from '../../queries/fetchCacheUserCompetencies.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_USER_COMPETENCIES_FILTERS,
  INITIAL_USER_COMPETENCIES_LIMIT,
  INITIAL_USER_COMPETENCIES_PAGE,
  INITIAL_USER_COMPETENCIES_SORT
} from '../../userCompetenciesConstants';

interface UserCompetenciesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface UserCompetenciesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface UserCompetenciesDefaultOptions {
  action?: string;
  cacheKey: FetchUserCompetenciesCacheKey;
  query: FetchUserCompetenciesGqlQuery;
  initialFilters?: FetchUserCompetenciesFilters;
  initialSort?: FetchUserCompetenciesSort;
  initialPage?: FetchUserCompetenciesPage;
  initialLimit?: FetchUserCompetenciesLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type UserCompetenciesOptions = UserCompetenciesDefaultOptions &
  (
    | UserCompetenciesWithPrefetchItemOptions
    | UserCompetenciesWithoutPrefetchItemOptions
  );

const scope = 'userCompetencies';

function useCachePaginatedUserCompetencies<
  FetchUserCompetenciesType = FetchCacheUserCompetenciesQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_USER_COMPETENCIES_FILTERS,
  initialSort = INITIAL_USER_COMPETENCIES_SORT,
  initialPage = INITIAL_USER_COMPETENCIES_PAGE,
  initialLimit = INITIAL_USER_COMPETENCIES_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: UserCompetenciesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchUserCompetenciesType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    userCompetenciesData: data,
    userCompetencies: items,
    userCompetenciesError: itemsError,
    userCompetenciesErrorMessage: itemsErrorMessage,
    userCompetenciesTotalCount: itemsTotalCount,
    userCompetenciesFetched: isFetched,
    userCompetenciesLoading: isLoading,
    userCompetenciesIsPlaceholderData: isPlaceholderData,
    userCompetenciesFilters: currentFilters,
    userCompetenciesSort: currentSort,
    userCompetenciesPage: currentPage,
    userCompetenciesLimit: currentLimit,
    updateUserCompetencyCache: updateItemCache,
    updateUserCompetenciesCache: updateItemsCache,
    filterUserCompetencies: filterItems,
    changeUserCompetenciesFilters: changeItemsFilters,
    clearUserCompetenciesFilters: clearItemsFilters,
    sortUserCompetencies: sortItems,
    paginateUserCompetencies: paginateItems,
    limitUserCompetencies: limitItems,
    prefetchUserCompetencies: prefetchItems,
    prefetchUserCompetency: prefetchItem
  };
}

export default useCachePaginatedUserCompetencies;
